import React from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  Chip,
} from "@mui/material";

import DnsIcon from "@mui/icons-material/Dns";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

function MetricBar({ label, value, color }) {
  const percent = Math.max(0, Math.min(100, value ?? 0));

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mb: 0.7 }}
      >
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>

        <Typography variant="body2" fontWeight={600}>
          {percent.toFixed(1)}%
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={percent}
        color={color}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "action.hover",
          "& .MuiLinearProgress-bar": {
            borderRadius: 4,
            transition: "width 200ms linear",
          },
        }}
      />
    </Box>
  );
}

function CpuGauge({ value }) {
  const percent = Math.max(0, Math.min(100, value ?? 0));

  const size = 130;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - percent / 100);

  const color =
    percent >= 80 ? "#ff4d4f" :
    percent >= 60 ? "#faad14" :
    "#63c174";

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        mx: "auto",
      }}
    >
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.10)"
          strokeWidth={stroke}
        />

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dashoffset 220ms linear, stroke 220ms linear",
          }}
        />
      </svg>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1 }}
        >
          CPU
        </Typography>

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {percent.toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );
}

export default function ServerCard({ server, connected }) {
  const { name, cpu, ram, rx, tx } = server;

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        height: "100%",
        boxSizing: "border-box",
        borderRadius: 3,
        transition: "border-color 0.2s",
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <DnsIcon color="primary" fontSize="large" />

            <Typography variant="h6" fontWeight={700}>
              {name}
            </Typography>
          </Stack>

          <Chip
            label={connected ? "Online" : "Offline"}
            color={connected ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Box sx={{ mb: 3 }}>
          <CpuGauge value={cpu} />
        </Box>

        <Stack spacing={2.5}>
          <MetricBar
            label="RAM"
            value={ram}
            color="info"
          />
        </Stack>

        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ArrowDownwardIcon
                color="success"
                fontSize="small"
              />

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Входящий
              </Typography>
            </Stack>

            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {(rx ?? 0).toFixed(2)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Мбит/с
            </Typography>
          </Box>

          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ArrowUpwardIcon
                color="info"
                fontSize="small"
              />

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Исходящий
              </Typography>
            </Stack>

            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {(tx ?? 0).toFixed(2)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Мбит/с
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}